import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Card, CustomPaginator, GenericTable, GenericTableProps } from '@cdaas/reusable-components-daas';
import { AdminProductOrder, AdminProductOrderListResponse } from '@cdaas/api-clients/dist/order-admin-openapi';
import { useTranslation } from 'react-i18next';

import { orderAdminProductOrderClient } from '../../../../api-client/order-admin-client';
import { DATE_AND_TIME_FORMAT } from '../../../../utils';
import PageTemplate, { PageTemplateTranslations } from '../PageTemplate/PageTemplate';
import { DropdownMultiselect, OptionListOption } from '@gaiads/telia-react-component-library';
import { debounce } from 'lodash';

export default function OrdersPage() {
  const { t } = useTranslation('backOffice');
  const pageTemplateTranslations: PageTemplateTranslations = {
    title: t('ordersPage.title'),
  };

  const optionList: OptionListOption[] = [
    {
      label: t('common.orderStatus.cannotBeProcessed'),
      value: 'ERROR',
      checked: true,
    },
    {
      label: t('common.orderStatus.processingOrder'),
      value: 'CREATED,CUSTOMER_APPROVAL_SKIPPED,FINANCING_NOT_NEEDED,CUSTOMER_APPROVAL_NOT_NEEDED',
    },
    {
      label: t('common.orderStatus.approved'),
      value: 'CUSTOMER_APPROVED,APPROVALS_READY,DELIVERY_DATA_UPDATED,LOGISTICS_ORDER_SUBMITTED',
    },
    {
      label: t('common.orderStatus.pendingApproval'),
      value: 'CUSTOMER_APPROVAL_REQUESTED,FINANCING_DEAL_CREATED',
    },
    {
      label: t('common.orderStatus.delivered'),
      value: 'LOGISTICS_ORDER_COMPLETED,FINANCED_ASSETS_UPDATED,ORDER_COMPLETE,ORDER_UPDATED_AFTER_COMPLETION',
    },
    {
      label: t('common.orderStatus.financingDeclined'),
      value: 'FINANCING_DENIED',
    },
    {
      label: t('common.orderStatus.approvalDeclined'),
      value: 'CUSTOMER_REJECTED',
    },
    {
      label: t('common.orderStatus.cancelled'),
      value: 'CANCELLED',
    },
    {
      label: t('common.orderStatus.returned'),
      value: 'RETURNED',
    },
  ];

  const [options, setOptions] = useState<OptionListOption[]>(optionList);

  const [ordersResponse, setOrdersResponse] = useState<AdminProductOrderListResponse | undefined>();
  const [ordersLoading, setOrdersLoading] = useState<GenericTableProps<AdminProductOrder>['loading']>('short');
  const [ordersApiError, setOrdersApiError] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(0);

  const history = useHistory();

  const statusLabels = () => {
    const statusLabelList = options.filter(option => option.checked).map(option => option.label);
    return statusLabelList.length > 0 ? statusLabelList.join(', ') : t('common.orderStatus.all');
  };

  const fetchOrders = async (page: number, orderStatusList: string[]): Promise<void> => {
    try {
      setOrdersLoading('short');
      const orders = (
        await orderAdminProductOrderClient.listProductOrder(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          orderStatusList.length > 0 ? orderStatusList : undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          page,
          10,
        )
      ).data;
      setOrdersResponse(orders);
    } catch {
      setOrdersApiError(true);
    }
    setOrdersLoading('none');
  };

  const debouncedFetchOrders = useMemo(() => debounce(fetchOrders, 1000), []);

  useEffect(() => {
    debouncedFetchOrders(
      pageNumber,
      options.filter(option => option.checked).flatMap(option => option.value.split(',')),
    );
  }, [debouncedFetchOrders, pageNumber, options]);

  const changeOptions = (newOptionList: OptionListOption[]) => {
    setPageNumber(0);
    setOptions(newOptionList);
  };

  return (
    <PageTemplate
      translations={pageTemplateTranslations}
      pageContent={
        <>
          <DropdownMultiselect
            label={t('common.orderStatus.select')}
            onChange={(event, selectedOption) => {
              const newOptionList = options.map(option => {
                if (option.value === selectedOption.value) {
                  return { ...option, checked: !option.checked };
                } else {
                  return option;
                }
              });
              changeOptions(newOptionList);
            }}
            onClear={() => {
              changeOptions(optionList);
            }}
            options={options}
            disabled={ordersLoading !== 'none'}
          />
          <Card
            title={`${t('ordersPage.cardTitle')}: ${statusLabels()}`}
            wrapperDivCustomProps={{ backgroundColor: 'white', padding: { size: 'lg' }, borderRadius: { size: 'md' } }}
            tag={
              ordersResponse?.contents?.length
                ? { text: t('ordersPage.tagCheck') }
                : ordersLoading !== 'none'
                  ? { text: t('ordersPage.tagLoading'), tagProps: { backgroundColor: 'orange400' } }
                  : undefined
            }
            isInitiallyOpen={true}
          >
            <GenericTable
              isGenericTableInsideCard={true}
              noDataText={t('ordersPage.table.noDataText')}
              errorText={ordersApiError ? t('ordersPage.table.errorText') : undefined}
              loading={ordersLoading}
              inputData={ordersResponse?.contents}
              rowConfigs={[
                {
                  header: t('ordersPage.table.headerOrganization'),
                  key: 'ordered_to.customer_name',
                  cellWidthMultiplier: 2,
                },
                {
                  header: t('ordersPage.table.headerOrderId'),
                  key: 'unique_id',
                  cellWidthMultiplier: 2,
                },
                {
                  header: t('ordersPage.table.headerColtOrderId'),
                  key: 'colt_hardware_delivery_id',
                },
                {
                  header: t('ordersPage.table.headerCreated'),
                  key: 'created',
                  formatValue(value) {
                    return DateTime.fromISO(value).toFormat(DATE_AND_TIME_FORMAT);
                  },
                },
              ]}
              buttonConfig={{
                name: t('ordersPage.table.rowButtonText'),
                buttonProps: {
                  iconName: 'chevron-right',
                  size: 'md',
                  iconOnRight: true,
                },
                buttonClickCallback: order =>
                  history.push(`/customer/${order.ordered_to.external_organization_id}/orders?order_id=${order.unique_id}`),
              }}
            />
            {!!ordersResponse?.page_metadata && ordersLoading === 'none' && (
              <CustomPaginator
                activatePageInfoObject={ordersResponse.page_metadata}
                onPageChange={pageChangeInfo => setPageNumber(pageChangeInfo.nextChosenPageNumber)}
                paginatorStyleProps={{
                  pageSelectorProps: { centered: false },
                }}
              />
            )}
          </Card>
        </>
      }
    />
  );
}
